import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ClockInput, CloseOrderInput, FinishPhaseInput, PausePhaseInput, StartPhaseInput, TerminalState } from '@ecinsa/shared';
import { ApiError, OfflineError, api, envelope, getToken, setToken, type Catalog } from './api';
import * as queue from './queue';

export interface Toast {
  id: string;
  kind: 'ok' | 'error' | 'info';
  title: string;
  body?: string;
}

interface TerminalContextValue {
  state: TerminalState | null;
  catalog: Catalog | null;
  online: boolean;
  queued: number;
  busy: boolean;
  toasts: Toast[];
  notify: (toast: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
  login: (pin: string) => Promise<void>;
  /** Entrar como un operario de demostración, sin PIN. */
  demoLogin: (employeeId: number) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  clock: (direction: 'in' | 'out', reasonCode?: string | null) => Promise<void>;
  startPhase: (input: Omit<StartPhaseInput, 'clientEventId' | 'occurredAt'>) => Promise<void>;
  pausePhase: (input: Omit<PausePhaseInput, 'clientEventId' | 'occurredAt'>) => Promise<void>;
  finishPhase: (input: Omit<FinishPhaseInput, 'clientEventId' | 'occurredAt'>) => Promise<void>;
  closeOrder: (input: Omit<CloseOrderInput, 'clientEventId' | 'occurredAt'>) => Promise<void>;
  flush: () => Promise<void>;
}

const TerminalContext = createContext<TerminalContextValue | null>(null);

export function useTerminal(): TerminalContextValue {
  const ctx = useContext(TerminalContext);
  if (!ctx) throw new Error('useTerminal fuera de TerminalProvider');
  return ctx;
}

export function TerminalProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<TerminalState | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(0);
  const [busy, setBusy] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const flushing = useRef(false);

  const notify = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = crypto.randomUUID();
    setToasts((current) => [...current, { ...toast, id }]);
    setTimeout(() => setToasts((current) => current.filter((t) => t.id !== id)), toast.kind === 'error' ? 7000 : 3500);
  }, []);

  const dismiss = useCallback((id: string) => setToasts((c) => c.filter((t) => t.id !== id)), []);

  const refreshQueueCount = useCallback(async () => {
    setQueued(await queue.count().catch(() => 0));
  }, []);

  /**
   * Vacía la cola local contra el servidor.
   *
   * Las acciones se mandan en bloque y en orden; el servidor responde por cada
   * una si la aceptó. Las aceptadas se borran de la cola; una rechazada por
   * regla de negocio (por ejemplo, una fase que otro ya finalizó) también se
   * borra y se avisa al operario, porque reintentarla eternamente no la va a
   * arreglar.
   */
  const flush = useCallback(async () => {
    if (flushing.current || !navigator.onLine || !getToken()) return;
    const actions = await queue.pending();
    if (actions.length === 0) return;

    flushing.current = true;
    try {
      const response = await api.sync(actions.map((a) => ({ kind: a.kind, payload: a.payload })));
      for (const result of response.results) {
        const action = actions[result.index];
        if (!action) continue;
        await queue.remove(action.id);
        if (!result.ok) {
          notify({
            kind: 'error',
            title: 'Acción rechazada al recuperar la conexión',
            body: `${action.description}: ${result.message ?? 'motivo desconocido'}`,
          });
        }
      }
      setState(response.state);
      const remaining = await queue.count();
      if (actions.length && remaining === 0) {
        notify({ kind: 'ok', title: 'Pendientes enviados', body: `${actions.length} acciones sincronizadas con SAP` });
      }
    } catch (err) {
      if (!(err instanceof OfflineError)) {
        notify({ kind: 'error', title: 'No se pudieron enviar los pendientes', body: (err as Error).message });
      }
    } finally {
      flushing.current = false;
      await refreshQueueCount();
    }
  }, [notify, refreshQueueCount]);

  // Conectividad: `navigator.onLine` solo detecta el cable; el estado real lo
  // marca si las peticiones funcionan, que es lo que actualiza `run`.
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void flush();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    const timer = setInterval(() => void flush(), 15_000);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      clearInterval(timer);
    };
  }, [flush]);

  useEffect(() => {
    void refreshQueueCount();
  }, [refreshQueueCount]);

  const refresh = useCallback(async () => {
    if (!getToken()) return;
    try {
      const [me, cat] = await Promise.all([api.me(), api.catalog()]);
      setState(me.state);
      setCatalog(cat);
      setOnline(true);
    } catch (err) {
      if (err instanceof OfflineError) setOnline(false);
      else if (err instanceof ApiError && err.status === 401) {
        setToken(null);
        setState(null);
      }
    }
  }, []);

  useEffect(() => {
    if (getToken()) void refresh();
  }, [refresh]);

  const login = useCallback(
    async (pin: string) => {
      setBusy(true);
      try {
        const { token, state: next } = await api.login(pin);
        setToken(token);
        setState(next);
        setOnline(true);
        const cat = await api.catalog().catch(() => null);
        if (cat) setCatalog(cat);
        await flush();
      } finally {
        setBusy(false);
      }
    },
    [flush],
  );

  const demoLogin = useCallback(
    async (employeeId: number) => {
      setBusy(true);
      try {
        const r = await api.demoLogin('operario', String(employeeId));
        setToken(r.token);
        if (r.state) setState(r.state);
        setOnline(true);
        const cat = await api.catalog().catch(() => null);
        if (cat) setCatalog(cat);
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const logout = useCallback(() => {
    setToken(null);
    setState(null);
  }, []);

  /**
   * Ejecuta una acción contra el servidor y, si no hay red, la encola
   * aplicando el efecto en local para que el terminal siga siendo coherente
   * delante del operario.
   */
  const run = useCallback(
    async (
      kind: queue.QueuedKind,
      payload: unknown,
      description: string,
      call: () => Promise<{ state: TerminalState }>,
      optimistic: (current: TerminalState) => TerminalState,
    ) => {
      setBusy(true);
      try {
        const response = await call();
        setState(response.state);
        setOnline(true);
      } catch (err) {
        if (err instanceof OfflineError) {
          setOnline(false);
          await queue.enqueue({
            id: crypto.randomUUID(),
            kind,
            payload,
            description,
            createdAt: new Date().toISOString(),
          });
          await refreshQueueCount();
          setState((current) => (current ? optimistic(current) : current));
          notify({
            kind: 'info',
            title: 'Guardado sin conexión',
            body: 'Se enviará a SAP en cuanto vuelva la red.',
          });
        } else {
          throw err;
        }
      } finally {
        setBusy(false);
      }
    },
    [notify, refreshQueueCount],
  );

  const clock = useCallback(
    async (direction: 'in' | 'out', reasonCode?: string | null) => {
      const input: ClockInput = { ...envelope(), direction, reasonCode: reasonCode ?? null };
      await run('clock', input, direction === 'in' ? 'Fichaje de entrada' : 'Fichaje de salida', () => api.clock(input), (current) => ({
        ...current,
        presence: { ...current.presence, inside: direction === 'in', since: input.occurredAt },
        activeSession: direction === 'out' ? null : current.activeSession,
        can: {
          ...current.can,
          clockIn: direction === 'out',
          clockOut: direction === 'in',
          startPhase: direction === 'in',
          finishPhase: direction === 'in',
          closeOrder: direction === 'in',
        },
      }));
    },
    [run],
  );

  const startPhase = useCallback(
    async (partial: Omit<StartPhaseInput, 'clientEventId' | 'occurredAt'>) => {
      const input: StartPhaseInput = { ...envelope(), ...partial };
      await run('startPhase', input, `Inicio de fase ${partial.phaseCode}`, () => api.startPhase(input), (current) => ({
        ...current,
        activeSession: {
          id: input.clientEventId,
          employeeId: current.employee.employeeId,
          employeeName: current.employee.fullName,
          orderDocEntry: input.orderDocEntry,
          orderDocNum: String(input.orderDocEntry),
          phaseCode: input.phaseCode,
          resourceCode: null,
          startedAt: input.occurredAt,
          endedAt: null,
          seconds: 0,
          status: 'running',
          stopReasonCode: null,
          stopReasonName: null,
        },
        can: { ...current.can, startPhase: false, pausePhase: true, finishPhase: false, closeOrder: false },
      }));
    },
    [run],
  );

  const pausePhase = useCallback(
    async (partial: Omit<PausePhaseInput, 'clientEventId' | 'occurredAt'>) => {
      const input: PausePhaseInput = { ...envelope(), ...partial };
      await run('pausePhase', input, 'Parada de fase', () => api.pausePhase(input), (current) => ({
        ...current,
        activeSession: null,
        can: { ...current.can, startPhase: true, pausePhase: false, finishPhase: true, closeOrder: true },
      }));
    },
    [run],
  );

  const finishPhase = useCallback(
    async (partial: Omit<FinishPhaseInput, 'clientEventId' | 'occurredAt'>) => {
      const input = { ...envelope(), ...partial } as FinishPhaseInput;
      await run('finishPhase', input, `Finalización de fase ${partial.phaseCode}`, () => api.finishPhase(input), (current) => current);
    },
    [run],
  );

  const closeOrder = useCallback(
    async (partial: Omit<CloseOrderInput, 'clientEventId' | 'occurredAt'>) => {
      const input = { ...envelope(), ...partial } as CloseOrderInput;
      await run('closeOrder', input, 'Cierre de orden', () => api.closeOrder(input), (current) => current);
    },
    [run],
  );

  const value = useMemo<TerminalContextValue>(
    () => ({
      state, catalog, online, queued, busy, toasts, notify, dismiss,
      login, demoLogin, logout, refresh, clock, startPhase, pausePhase, finishPhase, closeOrder, flush,
    }),
    [state, catalog, online, queued, busy, toasts, notify, dismiss, login, demoLogin, logout, refresh, clock, startPhase, pausePhase, finishPhase, closeOrder, flush],
  );

  return <TerminalContext.Provider value={value}>{children}</TerminalContext.Provider>;
}

/** Cierra la sesión del terminal tras `seconds` sin tocar la pantalla. */
export function useIdleLogout(seconds: number, onIdle: () => void): void {
  useEffect(() => {
    if (seconds <= 0) return;
    let timer = window.setTimeout(onIdle, seconds * 1000);
    const reset = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(onIdle, seconds * 1000);
    };
    const events = ['pointerdown', 'keydown', 'wheel'] as const;
    for (const event of events) window.addEventListener(event, reset, { passive: true });
    return () => {
      window.clearTimeout(timer);
      for (const event of events) window.removeEventListener(event, reset);
    };
  }, [seconds, onIdle]);
}
